import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import PageHeader from '../components/common/PageHeader';
import SectionHeading from '../components/ui/SectionHeading';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, MapPin, Grape, Wrench, Briefcase, Building2 } from 'lucide-react';

const faqData = [
  {
    question: "What is workflow automation for small businesses?",
    answer: "Workflow automation uses software and AI tools to handle repetitive business tasks — like sending follow-up emails, collecting documents, or updating records — without manual effort. It reduces errors, saves time, and lets your team focus on higher-value work."
  },
  {
    question: "How much does workflow automation cost in Marlborough?",
    answer: "Discovery and roadmap projects start from $900. Most SME automation builds range from $6,500 to $25,000+ depending on scope. Ongoing support is available from $120/month. A detailed ROI model is provided before any commitment."
  },
  {
    question: "Do I need technical skills to use automation?",
    answer: "No. Our solutions are designed for business users, not developers. Staff training is included in every project, and ongoing support ensures your team stays confident."
  },
  {
    question: "How long does it take to implement automation?",
    answer: "Most initial automation projects are live within 2–4 weeks. Complex integrations or regulated workflows may take longer, assessed during discovery."
  },
  {
    question: "Is my business data safe with AI automation?",
    answer: "Yes. We follow the SAFE-NZ framework, comply with the NZ Privacy Act 2020, and never use client data to train AI models. Data handling policies are documented during the strategy phase."
  },
  {
    question: "What industries in Marlborough benefit from automation?",
    answer: "Professional services, wine industry operations, trades businesses, tourism operators, and small service businesses all benefit significantly. Any business with repetitive admin tasks can see measurable improvements."
  },
  {
    question: "Can I start small and scale later?",
    answer: "Absolutely. Our method is designed to start with high-impact, low-risk automations and expand from there. Systems are built on scalable infrastructure."
  },
  {
    question: "What's the difference between AI and automation?",
    answer: "Automation handles rule-based, repetitive tasks (e.g., sending reminders). AI adds intelligence — understanding context, generating content, or making recommendations. Most business solutions combine both."
  }
];

const WorkflowAutomationMarlborough: React.FC = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "ThinkTomorrow.AI",
    "description": "Workflow automation and AI consulting for small businesses in Marlborough and Blenheim, New Zealand.",
    "url": "https://www.thinktomorrow.ai/workflow-automation-marlborough",
    "areaServed": [
      { "@type": "City", "name": "Blenheim" },
      { "@type": "AdministrativeArea", "name": "Marlborough" }
    ],
    "serviceType": "Workflow Automation Consulting"
  };

  return (
    <>
      <Helmet>
        <title>Workflow Automation Marlborough & Blenheim | ThinkTomorrow.AI</title>
        <meta name="description" content="Workflow automation for small businesses in Marlborough and Blenheim. Reduce admin, automate enquiries, booking and reporting. Discovery from $900." />
        <link rel="canonical" href="https://www.thinktomorrow.ai/workflow-automation-marlborough" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>
      <Navbar />
      <main className="min-h-screen pt-16 md:pt-24">
        <PageHeader
          title="Workflow Automation in Marlborough (Blenheim) for Small Businesses"
          subtitle="Reduce manual work, improve accuracy, and free your team to focus on what matters."
        />

        {/* What Workflow Automation Means */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">What Workflow Automation Means for Marlborough Businesses</h2>
            <p className="text-muted-foreground text-lg mb-4">
              Workflow automation replaces repetitive, manual tasks with smart systems that run reliably in the background. For Marlborough businesses — from Blenheim professional offices to vineyard operations — this means fewer errors, faster response times, and more capacity without hiring.
            </p>
            <p className="text-muted-foreground text-lg">
              Whether it's automatically following up on enquiries, collecting documents from clients, or generating weekly reports, automation lets your team spend time on work that actually grows the business.
            </p>
          </div>
        </section>

        {/* Common Workflows */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Common Workflows We Automate</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "Customer enquiries & lead capture",
                "Appointment booking & confirmations",
                "Weekly and monthly reporting",
                "Document collection & filing",
                "Internal admin & data entry",
                "Follow-ups & reminders"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-background rounded-lg border">
                  <CheckCircle2 className="text-brand-teal mt-0.5 shrink-0" size={20} />
                  <span className="text-foreground">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Industries in Marlborough We Help</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { icon: <Briefcase size={24} />, title: "Professional Services", desc: "Accountants, lawyers, architects, and consultants reducing admin overhead." },
                { icon: <Grape size={24} />, title: "Wine Industry Operations", desc: "Vineyard and cellar door operations streamlining bookings, compliance, and reporting." },
                { icon: <Wrench size={24} />, title: "Trades Businesses", desc: "Builders, electricians, and contractors automating quoting, scheduling, and follow-ups." },
                { icon: <Building2 size={24} />, title: "Small Service Businesses", desc: "Tourism, hospitality, and local services improving customer communication and admin." }
              ].map((industry, i) => (
                <div key={i} className="p-6 bg-muted/30 rounded-lg border">
                  <div className="text-brand-teal mb-3">{industry.icon}</div>
                  <h3 className="font-semibold text-foreground mb-2">{industry.title}</h3>
                  <p className="text-muted-foreground text-sm">{industry.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Method */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Responsible AI Implementation</h2>
            <p className="text-muted-foreground text-lg mb-8">Our 4-stage method ensures every automation is justified, tested, and delivers measurable value.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { num: "1", title: "Discovery", desc: "Evaluate workflows, identify opportunities, capture baseline metrics." },
                { num: "2", title: "Roadmap", desc: "Prioritise use-cases by ROI, design governance and human oversight." },
                { num: "3", title: "Implementation", desc: "Build, integrate, train staff, deploy with rollback plans." },
                { num: "4", title: "Optimisation", desc: "Monitor, review at 30/60/90 days, continuously improve." }
              ].map((step, i) => (
                <div key={i} className="text-center p-6 bg-background rounded-lg border">
                  <div className="w-10 h-10 rounded-full bg-brand-teal text-white flex items-center justify-center font-bold mx-auto mb-3">{step.num}</div>
                  <h3 className="font-semibold text-foreground mb-2">{step.title}</h3>
                  <p className="text-muted-foreground text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqData.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent>{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTAs */}
        <section className="py-16 bg-gradient-to-br from-[#0B1939] to-[#081631] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Automate Your Marlborough Business?</h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">Start with a structured discovery session. We'll identify your highest-impact opportunities and build a practical roadmap.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="primary" className="flex items-center gap-2">
                  Book Workflow Automation Discovery Call <ArrowRight size={16} />
                </Button>
              </Link>
              <Link to="/ai-solutions">
                <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-brand-navy">
                  Get AI Readiness Snapshot
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center justify-center gap-2 text-gray-400 text-sm">
              <MapPin size={14} />
              <span>Serving Marlborough, Blenheim, and wider New Zealand</span>
            </div>
          </div>
        </section>

        {/* Internal Links */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h3 className="font-semibold text-foreground mb-4">Related Pages</h3>
            <div className="flex flex-wrap gap-4">
              <Link to="/ai-automation-professional-services" className="text-brand-teal hover:underline text-sm">AI Automation for Professional Services →</Link>
              <Link to="/ai-workflow-automation-nz" className="text-brand-teal hover:underline text-sm">AI Workflow Automation NZ →</Link>
              <Link to="/case-studies/architect-workflow-intelligence" className="text-brand-teal hover:underline text-sm">Case Study: Architect Workflow Intelligence →</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default WorkflowAutomationMarlborough;
